import React from 'react';
import {
  ServiceOffersQuery,
  useBidServiceOfferMutation,
  useGet_Matching_Prisma_User_Or_DriverQuery,
  useGetDriverDrivingCompanyQuery,
  useGetDriverQuery,
  useGetMeQuery,
  useServiceOffersQuery,
  useUnbidServiceOfferMutation,
} from '../../graphql/datamodel.gen';
import { getClient } from '../../apollo';
import { Button, message, Popconfirm, Table, Tag, Tooltip } from 'antd';
import moment from 'moment/moment';
import { MainContext } from '../../contexts/mainContext';

const companyHasBidForService = (
  serviceBids: ServiceOffersQuery['serviceOffers'][0]['bids'],
  drivingCompanyId?: string,
): Boolean => {
  if (!drivingCompanyId) {
    return false;
  }
  return serviceBids.some((bid) => {
    return bid.drivingCompany.id === drivingCompanyId;
  });
};

interface OfferedServicesProps {}

export const OfferedServices: React.FC<OfferedServicesProps> = (
  props: OfferedServicesProps,
) => {
  const [rowKeyLoading, setRowKeyLoading] = React.useState(null);

  const serviceOffersQuery = useServiceOffersQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const getDriverQuery = useGetDriverQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const getDriverDrivingCompany = useGetDriverDrivingCompanyQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const bidServiceOfferMutation = useBidServiceOfferMutation({
    client: getClient(),
  });

  const unbidServiceOfferMutation = useUnbidServiceOfferMutation({
    client: getClient(),
  });

  const onBidServiceOffer = React.useCallback(
    (serviceOfferId: string) => {
      setRowKeyLoading(serviceOfferId);
      bidServiceOfferMutation[0]({
        variables: {
          id: serviceOfferId,
        },
        onCompleted: (data) => {
          message.success('Servicio postulado correctamente');
          setRowKeyLoading(null);
        },
        onError: () => {
          message.error('Ha ocurrido un error al realizar la operación');
          setRowKeyLoading(null);
        },
      });
    },
    [bidServiceOfferMutation[0]],
  );

  const onUnbidServiceOffer = React.useCallback(
    (serviceOfferId: string) => {
      setRowKeyLoading(serviceOfferId);
      unbidServiceOfferMutation[0]({
        variables: {
          id: serviceOfferId,
        },
        onCompleted: (data) => {
          message.success('Postulación eliminada correctamente');
          setRowKeyLoading(null);
        },
        onError: () => {
          message.error('Ha ocurrido un error al realizar la operación');
          setRowKeyLoading(null);
        },
      });
    },
    [unbidServiceOfferMutation[0]],
  );

  return (
    <div>
      <Table
        size={'small'}
        loading={serviceOffersQuery.loading}
        columns={[
          {
            title: 'ID',
            width: 100,
            dataIndex: ['service', 'volcanoServiceId'],
          },
          {
            title: 'Salida',
            width: 200,
            dataIndex: 'departure',
            key: 'departure',
            sortDirections: ['descend', 'ascend'],
            defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
            render: (
              _: any,
              record: ServiceOffersQuery['serviceOffers'][0],
            ) => {
              return (
                <div>
                  {record.service.departure.date}{' '}
                  <b>{record.service.departure.time}</b>
                </div>
              );
            },
            sorter: (
              a: ServiceOffersQuery['serviceOffers'][0],
              b: ServiceOffersQuery['serviceOffers'][0],
            ) => {
              if (
                a.service.departure &&
                a.service.departure.time &&
                a.service.departure.date &&
                b.service.departure &&
                b.service.departure.time &&
                b.service.departure.date
              ) {
                const aDateTime = new Date(a.service.departure.date);
                aDateTime.setHours(
                  Number((a.service.departure.time as string).split(':')[0]),
                  Number((a.service.departure.time as string).split(':')[1]),
                );

                const bDateTime = new Date(b.service.departure.date);
                bDateTime.setHours(
                  Number((b.service.departure.time as string).split(':')[0]),
                  Number((b.service.departure.time as string).split(':')[1]),
                );

                return bDateTime.getTime() - aDateTime.getTime();
              }

              return 0;
            },
          },
          {
            title: 'Llegada',
            dataIndex: 'arrival',
            key: 'arrival',
            width: 100,
            render: (
              _: any,
              record: ServiceOffersQuery['serviceOffers'][0],
            ) => {
              const arrivalDate = moment(record.service.arrival.date);
              const departureDate = moment(record.service.departure.date);
              const durationAsDays = moment
                .duration(arrivalDate.diff(departureDate))
                .asDays();

              if (durationAsDays >= 1) {
                // Append +1 to the time
                return (
                  <div>
                    <b>{record.service.arrival.time}</b>{' '}
                    <sup>+{durationAsDays}</sup>
                  </div>
                );
              } else {
                // Just show the time
                return (
                  <div>
                    <b>{record.service.arrival.time}</b>
                  </div>
                );
              }
            },
          },
          {
            title: '# PAX',
            dataIndex: ['service', 'numOfPassengers'],
            width: 75,
          },
          {
            title: 'Descripción',
            dataIndex: ['service', 'description'],
            ellipsis: {
              showTitle: false,
            },
            render: (value) => (
              <Tooltip placement="topLeft" title={value}>
                {value}
              </Tooltip>
            ),
          },
          {
            title: 'Tipo de vehículo',
            dataIndex: ['service', 'requestedVehicleDescription'],
            width: 200,
            ellipsis: {
              showTitle: false,
            },
            render: (value) => (
              <Tooltip placement="topLeft" title={value}>
                {value}
              </Tooltip>
            ),
          },
          {
            title: 'Postulado',
            width: 100,
            sorter: (
              recordA: ServiceOffersQuery['serviceOffers'][0],
              recordB: ServiceOffersQuery['serviceOffers'][0],
            ) => {
              const recordABid = companyHasBidForService(
                recordA.bids,
                getDriverDrivingCompany.data?.driver?.drivingCompany?.id,
              )
                ? 1
                : 0;
              const recordBBid = companyHasBidForService(
                recordB.bids,
                getDriverDrivingCompany.data?.driver?.drivingCompany?.id,
              )
                ? 1
                : 0;

              return recordABid - recordBBid;
            },
            render: (
              _: any,
              record: ServiceOffersQuery['serviceOffers'][0],
            ) => {
              if (
                companyHasBidForService(
                  record.bids,
                  getDriverDrivingCompany.data?.driver?.drivingCompany?.id,
                )
              ) {
                return <Tag color={'success'}>Sí</Tag>;
              }

              return <Tag>No</Tag>;
            },
          },
          {
            title: 'Estado',
            width: 150,
            render: (
              _: any,
              record: ServiceOffersQuery['serviceOffers'][0],
            ) => {
              const [bid] = record.bids;

              if (!bid) {
                return null;
              }

              if (bid.status === 'ACCEPTED') {
                return <Tag color={'success'}>Aceptada</Tag>;
              }

              if (bid.status === 'REJECTED') {
                return <Tag color={'error'}>Rechazada</Tag>;
              }

              if (bid.status === 'PENDING') {
                return <Tag color={'blue'}>Pendiente</Tag>;
              }
            },
          },
          {
            title: 'Acciones',
            width: 200,
            render: (
              _: any,
              record: ServiceOffersQuery['serviceOffers'][0],
            ) => {
              if (
                companyHasBidForService(
                  record.bids,
                  getDriverDrivingCompany.data?.driver?.drivingCompany?.id,
                )
              ) {
                const [bid] = record.bids;

                return (
                  <Popconfirm
                    title={
                      '¿Estás seguro de que deseas dejar de postular para este servicio?'
                    }
                    onConfirm={() => {
                      onUnbidServiceOffer(record.id);
                    }}
                  >
                    <Button
                      danger
                      loading={record.id === rowKeyLoading}
                      disabled={
                        bid.status === 'REJECTED' || bid.status === 'ACCEPTED'
                      }
                    >
                      Eliminar postulación
                    </Button>
                  </Popconfirm>
                );
              }

              return (
                <Button
                  type={'primary'}
                  onClick={() => {
                    onBidServiceOffer(record.id);
                  }}
                  loading={record.id === rowKeyLoading}
                >
                  Postular
                </Button>
              );
            },
          },
        ]}
        dataSource={serviceOffersQuery?.data?.serviceOffers}
      />
    </div>
  );
};
